import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";

export const RECEIPTS_BUCKET = "subscription-receipts";
export const MAX_PAYMENT_ATTACHMENTS = 5;

export interface PaymentAttachment {
  id: string;
  payment_request_id: string;
  file_path: string;
  file_name: string;
  file_type: string;
  file_size: number;
  sort_order: number;
  created_at: string;
}

export type RefundStatus = "none" | "pending_refund" | "refunded" | "refund_rejected";

export interface PaymentRequestRow {
  id: string;
  subscription_id: string;
  owner_user_id: string;
  plan_id: string;
  billing_cycle: "monthly" | "yearly";
  amount: number;
  subtotal?: number | null;
  currency: string;
  provider: string;
  status: "pending" | "approved" | "rejected" | "canceled";
  reference_code: string | null;
  transfer_date: string | null;
  sender_bank: string | null;
  receipt_path: string | null;
  rejection_reason: string | null;
  is_upgrade?: boolean;
  prorated_credit?: number;
  refund_status?: RefundStatus;
  refund_reason?: string | null;
  refund_requested_at?: string | null;
  created_at: string;
  subscription_invoices?: any[] | null;
  attachments?: PaymentAttachment[];
}

export const pendingPaymentRequestIds = (requests: PaymentRequestRow[] | undefined): string[] =>
  (requests || []).filter((request) => request.status === "pending").map((request) => request.id).sort();

export const didPendingPaymentResolve = (previousIds: string[], currentIds: string[]): boolean =>
  previousIds.some((id) => !currentIds.includes(id));


export interface OrgIdentity {
  legal_name_ar: string | null;
  legal_name_en: string | null;
  cr_number: string | null;
  vat_number: string | null;
  addr_short: string | null;
  addr_building: string | null;
  addr_street: string | null;
  addr_district: string | null;
  addr_secondary: string | null;
  addr_postal_code: string | null;
  addr_city: string | null;
  addr_country: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  website: string | null;
}

export interface BillingSettings extends OrgIdentity {
  bank_name_ar: string | null;
  bank_name_en: string | null;
  beneficiary_name: string | null;
  iban: string | null;
  instructions_ar: string | null;
  instructions_en: string | null;
  instructions_fr: string | null;
  vat_rate: number;
}

export const ORG_IDENTITY_COLUMNS =
  "legal_name_ar, legal_name_en, cr_number, vat_number, addr_short, addr_building, addr_street, addr_district, addr_secondary, addr_postal_code, addr_city, addr_country, contact_phone, contact_email, website";

/** Public-safe subset of billing settings (no bank/IBAN). Readable by visitors. */
export const useOrgIdentity = () =>
  useQuery({
    queryKey: ["org-identity"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<OrgIdentity | null> => {
      const { data, error } = await supabase.rpc("get_org_identity_public");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as OrgIdentity) ?? null;
    },
  });

/** Subscriber-safe transfer details (bank + IBAN + instructions only). */
export const useBillingTransferInfo = (enabled = true) =>
  useQuery({
    queryKey: ["billing-transfer-info"],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Partial<BillingSettings> | null> => {
      const { data, error } = await (supabase as any).rpc("get_billing_transfer_info");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as Partial<BillingSettings>) ?? null;
    },
  });

/** Staff-only: full billing settings row. */
export const useBillingSettings = () =>
  useQuery({
    queryKey: ["billing-settings"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<BillingSettings | null> => {
      const { data, error } = await (supabase as any)
        .from("billing_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (error) throw error;
      return (data as BillingSettings) ?? null;
    },
  });


/**
 * Payment requests of one subscription (the open workspace), not of the
 * signed-in user's personal account. Every participant of that subscription
 * sees the same list; the database enforces the membership check.
 */
export const usePaymentRequests = (subscriptionId: string | undefined, enabled = true) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["payment-requests", subscriptionId],
    enabled: !!user && !!subscriptionId && enabled,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const requests = query.state.data as PaymentRequestRow[] | undefined;
      return requests?.some((request) => request.status === "pending") ? 5_000 : false;
    },
    queryFn: async (): Promise<PaymentRequestRow[]> => {
      if (!user || !subscriptionId) return [];
      const { data, error } = await (supabase as any)
        .rpc("list_my_payment_requests", { _subscription_id: subscriptionId })
        .select(
          "*, subscription_invoices!payment_request_id(invoice_number, amount, subtotal, vat_amount, vat_rate, period_start, period_end, paid_at, currency)",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return attachPaymentFiles((data || []) as PaymentRequestRow[]);
    },
  });
};

export const useSubscriptionPaymentSync = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: sub } = useSubscription();
  const { data: requests } = usePaymentRequests(sub?.id);
  const previousPendingIds = useRef<string[] | null>(null);

  useEffect(() => {
    if (!user || !requests) return;

    const currentPendingIds = pendingPaymentRequestIds(requests);
    const previousIds = previousPendingIds.current;
    previousPendingIds.current = currentPendingIds;

    if (previousIds && didPendingPaymentResolve(previousIds, currentPendingIds)) {
      void queryClient.invalidateQueries({ queryKey: ["subscription"] });
      void queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["payment-requests"] });
    }
  }, [queryClient, requests, user]);
};

export interface CreatePaymentRequestInput {
  subscription_id: string;
  plan_id: string;
  billing_cycle: "monthly" | "yearly";
  receipts: File[];
}

const attachPaymentFiles = async <T extends PaymentRequestRow>(rows: T[]): Promise<T[]> => {
  if (rows.length === 0) return rows;
  const { data, error } = await (supabase as any)
    .from("subscription_payment_attachments")
    .select("id, payment_request_id, file_path, file_name, file_type, file_size, sort_order, created_at")
    .in("payment_request_id", rows.map((row) => row.id))
    .order("sort_order", { ascending: true });
  if (error) throw error;
  const attachments = (data || []) as PaymentAttachment[];
  return rows.map((row) => ({
    ...row,
    attachments: attachments.filter((attachment) => attachment.payment_request_id === row.id),
  }));
};

export const useCreatePaymentRequest = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePaymentRequestInput): Promise<PaymentRequestRow> => {
      if (!user) throw new Error("Not authenticated");

      if (input.receipts.length < 1 || input.receipts.length > MAX_PAYMENT_ATTACHMENTS) {
        throw new Error("Attach between 1 and 5 files");
      }

      const requestId = crypto.randomUUID();
      const uploadedPaths: string[] = [];
      try {
        const attachments = [];
        for (const [index, receipt] of input.receipts.entries()) {
          const ext = receipt.name.split(".").pop()?.toLowerCase() || "bin";
          const path = `${user.id}/${requestId}/${crypto.randomUUID()}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from(RECEIPTS_BUCKET)
            .upload(path, receipt, { upsert: false, contentType: receipt.type });
          if (upErr) throw upErr;
          uploadedPaths.push(path);
          attachments.push({
            file_path: path,
            file_name: receipt.name,
            file_type: receipt.type,
            file_size: receipt.size,
            sort_order: index,
          });
        }

        const { data, error } = await (supabase as any).rpc(
          "create_subscription_payment_request_with_attachments",
          {
            _request_id: requestId,
            _subscription_id: input.subscription_id,
            _plan_id: input.plan_id,
            _billing_cycle: input.billing_cycle,
            _attachments: attachments,
          },
        );
        if (error) throw error;
        const row = data as PaymentRequestRow;
        row.attachments = attachments.map((attachment, index) => ({
          id: `${requestId}-${index}`,
          payment_request_id: requestId,
          created_at: new Date().toISOString(),
          ...attachment,
        }));
        return row;
      } catch (error) {
        if (uploadedPaths.length > 0) {
          await supabase.storage.from(RECEIPTS_BUCKET).remove(uploadedPaths);
        }
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-requests"] });
    },
  });
};

export const useCancelPaymentRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase as any)
        .from("subscription_payment_requests")
        .update({ status: "canceled" })
        .eq("id", id)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("NOT_ALLOWED_OR_NOT_PENDING");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-requests"] });
    },
  });
};

export const getReceiptSignedUrl = async (path: string): Promise<string | null> => {
  const { data, error } = await supabase.storage.from(RECEIPTS_BUCKET).createSignedUrl(path, 300);
  if (error) return null;
  return data?.signedUrl ?? null;
};

export interface AdminPaymentRequestRow extends PaymentRequestRow {
  owner_email: string | null;
  owner_name: string | null;
  plan_name_ar: string | null;
  plan_name_en: string | null;
  reviewed_at: string | null;
  invoice_paid_at?: string | null;
}

export const useAdminPaymentRequests = (status: string) =>
  useQuery({
    queryKey: ["admin-payment-requests", status],
    queryFn: async (): Promise<AdminPaymentRequestRow[]> => {
      const { data, error } = await (supabase as any).rpc("admin_list_payment_requests", {
        _status: status || null,
        _limit: 200,
        _offset: 0,
      });
      if (error) throw error;
      return attachPaymentFiles((data || []) as AdminPaymentRequestRow[]);
    },
  });

export const useReviewPaymentRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      request_id: string;
      action: "approve" | "reject" | "refund" | "reject_refund";
      rejection_reason?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("review-subscription-payment", { body: vars });
      if (error) throw error;
      if (data && (data as any).error) throw new Error(String((data as any).error));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-payment-requests"] });
      qc.invalidateQueries({ queryKey: ["subscription"] });
    },
  });
};

/** Owner-side refund request: only flips refund_status to pending_refund. */
export const useRequestRefund = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; reason: string }) => {
      const { data, error } = await (supabase as any)
        .from("subscription_payment_requests")
        .update({ refund_status: "pending_refund", refund_reason: vars.reason.trim() })
        .eq("id", vars.id)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("REFUND_NOT_ALLOWED");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-requests"] });
    },
  });
};

/** True while the paid invoice of this request is still inside the 14-day refund window. */
export const REFUND_WINDOW_DAYS = 14;
export const canRequestRefund = (r: PaymentRequestRow): boolean => {
  if (r.status !== "approved" || (r.refund_status ?? "none") !== "none") return false;
  const paidAt = r.subscription_invoices?.[0]?.paid_at;
  if (!paidAt) return false;
  const days = (Date.now() - new Date(paidAt).getTime()) / 86400000;
  return days <= REFUND_WINDOW_DAYS;
};

